/**
 * @fileoverview Shared hook for POI form field management
 *
 * Consolidates:
 *  - Focus tracking to prevent overwriting user input during external updates
 *  - Change detection to avoid redundant saves (only PATCH when value actually changes)
 *  - Field-level validation (latitude/longitude ranges, character limits)
 *  - Triple optimistic update pattern:
 *    1. Redux store (immediate UI update)
 *    2. RTK Query cache (keeps cache in sync)
 *    3. Backend persistence (PATCH request)
 *  - Automatic rollback on save failure (all three layers)
 *
 * @example Basic usage
 * ```typescript
 * const { handleFocus, handleBlur, getFieldValue } = usePoiFormField({
 *   currentPOI,
 *   surveyId,
 * });
 *
 * <EmeraldInput
 *   value={getFieldValue(currentPOI?.addressLine1, "addressLine1")}
 *   onFocus={() => handleFocus("addressLine1", currentPOI?.addressLine1)}
 *   onBlur={() => handleBlur("addressLine1")}
 * />
 * ```
 *
 * @example Custom validation
 * ```typescript
 * const { validateField } = usePoiFormField({
 *   currentPOI,
 *   surveyId,
 *   validators: {
 *     customField: (value) => ({
 *       isValid: value.length <= 100,
 *       error: "Custom field must be 100 characters or less"
 *     })
 *   }
 * });
 * ```
 */

import { updatePOI as updatePOIAction } from "@/features/survey/poisSlice";
import { useAppDispatch } from "@app/hooks";
import { surveyPOIApi, useUpdatePOIMutation } from "@services/survey-poi";
import type {
  SurveyPOIRequest,
  SurveyPOIWithCategory,
} from "@survey-types/survey.poi.types";
import React, { useEffect, useRef, useState } from "react";

// Types

interface FocusedField {
  key: string;
  value: string;
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

interface FieldValidator {
  (value: string): ValidationResult;
}

interface UsePoiFormFieldParams {
  currentPOI: SurveyPOIWithCategory | undefined;
  surveyId: string | null;
  validators?: Partial<Record<keyof SurveyPOIRequest, FieldValidator>>;
}

interface UsePoiFormFieldReturn {
  // State (read-only exposure)
  focusedField: string | null;
  formErrors: Partial<Record<keyof SurveyPOIRequest, string>>;

  // Refs (exposed for component integration)
  originalValuesRef: React.MutableRefObject<SurveyPOIRequest>;

  // Helpers
  getFieldValue: (
    poiValue: string | undefined,
    field: keyof SurveyPOIRequest,
  ) => string;

  handleFocus: (field: keyof SurveyPOIRequest, currentValue?: string) => void;

  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof SurveyPOIRequest,
  ) => void;

  handleBlur: (
    field: keyof SurveyPOIRequest,
    valueOverride?: string,
    programmatic?: boolean,
  ) => Promise<void>;

  validateField: (field: keyof SurveyPOIRequest, value: string) => boolean;

  clearFieldError: (field: keyof SurveyPOIRequest) => void;
}

// Default form values

const defaultFormValues: SurveyPOIRequest = {
  categoryId: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  latitude: "",
  longitude: "",
  locationNotes: "",
};

// Built-in validators

const defaultValidators: Record<string, FieldValidator> = {
  latitude: (value: string): ValidationResult => {
    if (!value) return { isValid: true }; // Optional field
    const lat = Number(value);
    if (Number.isNaN(lat) || lat < -90 || lat > 90) {
      return {
        isValid: false,
        error: "Latitude must be between -90 and 90.",
      };
    }
    return { isValid: true };
  },

  longitude: (value: string): ValidationResult => {
    if (!value) return { isValid: true }; // Optional field
    const lng = Number(value);
    if (Number.isNaN(lng) || lng < -180 || lng > 180) {
      return {
        isValid: false,
        error: "Longitude must be between -180 and 180.",
      };
    }
    return { isValid: true };
  },

  locationNotes: (value: string): ValidationResult => {
    if (value.length > 500) {
      return {
        isValid: false,
        error: "Location notes cannot exceed 500 characters.",
      };
    }
    return { isValid: true };
  },
};

/**
 * Hook for managing POI form field state, validation, and persistence.
 *
 * @param params - Configuration object
 * @param params.currentPOI - The POI being edited
 * @param params.surveyId - Current survey ID
 * @param params.validators - Optional custom validators for specific fields
 *
 * @returns Object containing field handlers, validation, and state
 *
 * @see {@link usePropertyFormField} for similar pattern used in property forms
 */
export const usePoiFormField = (
  params: UsePoiFormFieldParams,
): UsePoiFormFieldReturn => {
  const { currentPOI, surveyId, validators = {} } = params;

  // RTK mutations
  const dispatch = useAppDispatch();
  const [updatePOI] = useUpdatePOIMutation();

  // State
  const [focusedField, setFocusedField] = useState<FocusedField | null>(null);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof SurveyPOIRequest, string>>
  >({});
  const originalValuesRef = useRef<SurveyPOIRequest>(defaultFormValues);

  // Initialize ref when currentPOI changes
  useEffect(() => {
    if (currentPOI) {
      originalValuesRef.current = {
        categoryId: currentPOI.categoryId || "",
        addressLine1: currentPOI.addressLine1 || "",
        addressLine2: currentPOI.addressLine2 || "",
        city: currentPOI.city || "",
        state: currentPOI.state || "",
        postalCode: currentPOI.postalCode || "",
        latitude: currentPOI.latitude || "",
        longitude: currentPOI.longitude || "",
        locationNotes: currentPOI.locationNotes || "",
      };
    }
  }, [currentPOI, currentPOI?.extId]);

  // Helpers

  const getFieldValue = (
    poiValue: string | undefined,
    field: keyof SurveyPOIRequest,
  ): string => {
    if (focusedField?.key === field) return focusedField.value;
    return poiValue ?? "";
  };

  const handleFocus = (
    field: keyof SurveyPOIRequest,
    currentValue?: string,
  ) => {
    setFocusedField({ key: field, value: currentValue ?? "" });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof SurveyPOIRequest,
  ) => {
    setFocusedField({ key: field, value: e.target.value });
    // Clear error when user types
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateField = (
    field: keyof SurveyPOIRequest,
    value: string,
  ): boolean => {
    // Merge default + custom validators
    const validator = validators[field] || defaultValidators[field];

    if (!validator) return true; // No validator = valid

    const result = validator(value);

    if (!result.isValid) {
      setFormErrors((prev) => ({ ...prev, [field]: result.error }));
      return false;
    }

    return true;
  };

  const clearFieldError = (field: keyof SurveyPOIRequest) => {
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleBlur = async (
    field: keyof SurveyPOIRequest,
    valueOverride?: string,
    programmatic?: boolean,
  ) => {
    if (!surveyId || !currentPOI) return;

    // Extract current value
    const currentValue =
      valueOverride !== undefined
        ? valueOverride.trim()
        : focusedField?.key === field
          ? focusedField.value.trim()
          : originalValuesRef.current[field]?.toString() || "";
    const originalValue = originalValuesRef.current[field]?.toString() || "";

    // Clear focus state only for user-initiated blurs
    if (!programmatic) {
      setFocusedField(null);
    }

    // Skip if unchanged
    if (currentValue === originalValue) return;

    // Validate before saving
    if (!validateField(field, currentValue)) return;

    let cacheUpdateResult: { undo: () => void } | undefined;

    try {
      // 1. Optimistically update Redux store
      dispatch(
        updatePOIAction({
          extId: currentPOI.extId,
          updates: { [field]: currentValue || undefined },
        }),
      );

      // 2. Optimistically update RTK Query cache
      cacheUpdateResult = dispatch(
        surveyPOIApi.util.updateQueryData("getPOIs", surveyId, (draft) => {
          const poi = draft.find((p) => p.extId === currentPOI.extId);
          if (poi) {
            (poi as unknown as Record<string, unknown>)[field] =
              currentValue || undefined;
          }
        }),
      );

      // 3. Persist to backend
      await updatePOI({
        surveyId,
        poiId: currentPOI.extId,
        body: { [field]: currentValue || undefined },
      }).unwrap();

      // Update ref only after successful save
      originalValuesRef.current = {
        ...originalValuesRef.current,
        [field]: currentValue,
      };
    } catch (error) {
      console.error(`Failed to update POI ${field}:`, error);

      // Rollback RTK Query cache
      cacheUpdateResult?.undo();

      // Rollback Redux store
      dispatch(
        updatePOIAction({
          extId: currentPOI.extId,
          updates: { [field]: originalValue || undefined },
        }),
      );

      // Rollback ref
      originalValuesRef.current = {
        ...originalValuesRef.current,
        [field]: originalValue,
      };

      // Show error
      setFormErrors((prev) => ({
        ...prev,
        [field]: "Failed to save. Please try again.",
      }));
    }
  };

  return {
    focusedField: focusedField?.key || null,
    formErrors,
    originalValuesRef,
    getFieldValue,
    handleFocus,
    handleInputChange,
    handleBlur,
    validateField,
    clearFieldError,
  };
};
